import axios from 'axios';
import { publicRuntimeConfig } from '@next-common/config-service';
import { createLogger } from '@next-common/logger';
import { forwardAPIResponseHttpHeaderToBrowser } from './getServerSidePropsLoader';
import { extractHeadersForWs } from './genericService';
const FALLBACK_LOGIN_PAGE_URL = '/connect?action=redirect&url=';
export const createAxiosInstance = ({ context, bearerToken, moduleName = '', user, hideHeaderInLogger, hideCookieAuthInLogger, log404AsDebug, }) => {
    const logger = createLogger(`${moduleName}`);
    let addRequestHeader = {};
    if (context) {
        addRequestHeader = extractHeadersForWs(context.req.headers);
    }
    const axiosInstance = axios.create({
        timeout: Number(publicRuntimeConfig.timeout.default),
        headers: bearerToken
            ? Object.assign(Object.assign({}, addRequestHeader), { Authorization: `Bearer ${bearerToken}` }) : addRequestHeader,
    });
    let remoteAddress;
    if (context) {
        const forwarded = context.req.headers['x-forwarded-for'];
        remoteAddress = Array.isArray(forwarded) ? forwarded[0] : context.req.socket.remoteAddress;
    }
    axiosInstance.interceptors.request.use(function (config) {
        var _a, _b;
        config.metadata = {
            startTime: new Date().getTime(),
        };
        logger.debug('Axios ssr config', {
            method: (_a = config === null || config === void 0 ? void 0 : config.method) === null || _a === void 0 ? void 0 : _a.toLocaleUpperCase(),
            url: config.url,
            headers: hideHeaderInLogger ? '' : JSON.stringify(config.headers),
            userId: user ? user.userId : '',
            login: user ? user.firstNameOrPseudo : '',
            'auth-cookie': hideCookieAuthInLogger ? '' : (_b = context === null || context === void 0 ? void 0 : context.req) === null || _b === void 0 ? void 0 : _b.cookies['auth'],
            ip: remoteAddress,
        });
        return config;
    });
    axiosInstance.interceptors.response.use(function (response) {
        var _a, _b, _c;
        const endTime = new Date().getTime();
        const metadata = response.config.metadata;
        if (context) {
            forwardAPIResponseHttpHeaderToBrowser(response.headers, context);
        }
        logger.debug('Axios ssr response', {
            callDuration: `${endTime - metadata.startTime} ms`,
            status: (response === null || response === void 0 ? void 0 : response.status) || '',
            method: (_b = (_a = response === null || response === void 0 ? void 0 : response.config) === null || _a === void 0 ? void 0 : _a.method) === null || _b === void 0 ? void 0 : _b.toLocaleUpperCase(),
            url: response.config.url,
            userId: user ? user.userId : '',
            login: user ? user.firstNameOrPseudo : '',
            'auth-cookie': hideCookieAuthInLogger ? '' : (_c = context === null || context === void 0 ? void 0 : context.req) === null || _c === void 0 ? void 0 : _c.cookies['auth'],
            ip: remoteAddress,
        });
        return response;
    }, function (err) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const metadata = err.config.metadata;
        const endTime = new Date().getTime();
        const logginErrorMetadata = {
            callDuration: `${endTime - metadata.startTime} ms`,
            status: ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) || '',
            method: err === null || err === void 0 ? void 0 : err.config.method.toLocaleUpperCase(),
            url: err === null || err === void 0 ? void 0 : err.config.url,
            userId: user ? user.userId : '',
            login: user ? user.firstNameOrPseudo : '',
            'auth-cookie': hideCookieAuthInLogger ? '' : (_b = context === null || context === void 0 ? void 0 : context.req) === null || _b === void 0 ? void 0 : _b.cookies['auth'],
            ip: remoteAddress,
        };
        if (((_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.status) === 401) {
            const errors = (_d = err.response.data) === null || _d === void 0 ? void 0 : _d.errors;
            let level = 'info';
            let loginPageUrl = FALLBACK_LOGIN_PAGE_URL;
            if (Array.isArray(errors) && ((_e = errors[0]) === null || _e === void 0 ? void 0 : _e.links) && ((_f = errors[0].links) === null || _f === void 0 ? void 0 : _f.login)) {
                level = 'warn';
                loginPageUrl = (_g = errors[0].links) === null || _g === void 0 ? void 0 : _g.login;
            }
            const resolvedUrl = (context === null || context === void 0 ? void 0 : context.resolvedUrl) || '';
            const destination = `${loginPageUrl}${encodeURIComponent(resolvedUrl)}`;
            logger[level]('Axios ssr 401 error', logginErrorMetadata);
            return Promise.reject({
                redirect: {
                    destination: destination,
                    basePath: false,
                    permanent: false,
                },
            });
        }
        else if (((_h = err === null || err === void 0 ? void 0 : err.response) === null || _h === void 0 ? void 0 : _h.status) === 404) {
            if (log404AsDebug) {
                logger.debug('Axios ssr 404 error', Object.assign({}, logginErrorMetadata));
            }
            else {
                logger.info('Axios ssr 404 error', Object.assign({}, logginErrorMetadata));
            }
        }
        else {
            logger.error('Axios ssr other errors', Object.assign(Object.assign({}, logginErrorMetadata), { exception: err.message || JSON.stringify(err) }));
        }
        return Promise.reject(err);
    });
    return axiosInstance;
};
